import { useState, useEffect, useCallback } from "react";
import Button from "components/Buttons/Button";
import IconButton from "components/Buttons/IconButton";
import InputField from "components/FormFields/InputField";
import TextAreaField from "components/FormFields/TextAreaField";
import { ReactSelect } from "components/FormFields/DropdownField";
import { AddCircle20Regular } from "@fluentui/react-icons";
import { IoTrashOutline } from "react-icons/io5";
import { http } from "middleware/apiMiddleware";
import { toast } from "react-hot-toast";
import { createSubscription, updateSubscription } from "constants/apiUrls";
import ModalComponent from "components/Common/Modal";
import Render from "components/Common/Render";

interface IAddSubscriptionTierModalProps {
  onClose: () => void;
  onSubmit: (data: any) => void;
  businessUuid: string;
  initialData?: any;
  onCloseAttempt?: (closeCallback: () => void) => void;
}

const cycleOptions = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
  { value: "3_MONTH", label: "3 Months" },
  { value: "6_MONTH", label: "6 Months" },
  { value: "YEARLY", label: "Yearly" },
];

const redemptionCycleOptions = [
  { value: "PER_HOUR", label: "Hour" },
  { value: "PER_DAY", label: "Day" },
  { value: "PER_WEEK", label: "Week" },
  { value: "PER_MONTH", label: "Month" },
];

// Generate item quantity options: 1-99 + *
const itemQuantityOptions = [
  ...Array.from({ length: 99 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) })),
  { value: "*", label: "*" }
];

// Generate limit options: 1-9
const limitOptions = Array.from({ length: 9 }, (_, i) => ({ 
  value: String(i + 1), 
  label: String(i + 1) 
}));

const AddSubscriptionTierModal: React.FC<IAddSubscriptionTierModalProps> = ({
  onClose,
  onSubmit,
  businessUuid,
  initialData,
  onCloseAttempt,
}) => {
  // Helper function to find option by value
  const findOption = (options: any[], value: string) => {
    return options.find(opt => opt.value === value) || null;
  };

  // Initialize form data with initialData if provided
  const getInitialFormData = () => {
    // Handle item quantity - could be a number or "*"
    let itemQuantityOption = null;
    if (initialData?.items?.[0]?.quantity) {
      const quantity = initialData.items[0].quantity;
      const quantityValue = quantity === "*" ? "*" : String(quantity);
      itemQuantityOption = findOption(itemQuantityOptions, quantityValue);
    }
    
    // Handle max redemption limit
    let maxRedemptionLimitOption = null;
    if (initialData?.items?.[0]?.limit) {
      maxRedemptionLimitOption = findOption(limitOptions, String(initialData.items[0].limit));
    }
    
    return {
    subscriptionName: initialData?.title || "",
    subscriptionDescription: initialData?.description || "",
    price: initialData?.price?.toString() || "",
    cycle: initialData?.cycle ? findOption(cycleOptions, initialData.cycle) : findOption(cycleOptions, "MONTHLY"),
    itemName: initialData?.items?.[0]?.itemName || "",
      itemQuantity: itemQuantityOption,
      maxRedemptionLimit: maxRedemptionLimitOption,
    maxRedemptionLimitCycle: initialData?.items?.[0]?.limitPeriod ? findOption(redemptionCycleOptions, initialData.items[0].limitPeriod) : findOption(redemptionCycleOptions, "PER_DAY"),
    maxSubscribers: initialData?.maxSubscribers?.toString() || "",
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [instructions, setInstructions] = useState<string[]>(initialData?.rules || []);
  const [instructionInputs, setInstructionInputs] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isActive, setIsActive] = useState(initialData?.isActive ?? true);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);

  // Check if form has any data filled
  const hasFormData = () => {
    const initialFormData = getInitialFormData();
    
    // Check if any field has been modified from initial state
    return (
      formData.subscriptionName !== initialFormData.subscriptionName ||
      formData.subscriptionDescription !== initialFormData.subscriptionDescription ||
      formData.price !== initialFormData.price ||
      formData.cycle?.value !== initialFormData.cycle?.value ||
      formData.itemName !== initialFormData.itemName ||
      formData.itemQuantity?.value !== initialFormData.itemQuantity?.value ||
      formData.maxRedemptionLimit?.value !== initialFormData.maxRedemptionLimit?.value ||
      formData.maxRedemptionLimitCycle?.value !== initialFormData.maxRedemptionLimitCycle?.value ||
      formData.maxSubscribers !== initialFormData.maxSubscribers ||
      instructions.length > (initialData?.rules || []).length ||
      instructionInputs.some(input => input.trim() !== '')
    );
  };

  // Handle close/cancel with confirmation if form has data
  const handleCloseAttempt = useCallback(() => {
    if (hasFormData()) {
      setShowCancelConfirmModal(true);
    } else {
      onClose();
    }
  }, [formData, instructions, instructionInputs, initialData, onClose]);

  // Expose the close handler to parent component
  useEffect(() => {
    if (onCloseAttempt) {
      onCloseAttempt(handleCloseAttempt);
    }
  }, [handleCloseAttempt, onCloseAttempt]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    // Validate business UUID first
    if (!businessUuid) {
      toast.error("Business information is missing. Please try again.");
      return;
    }

    // Save any pending instructions
    const validInstructions = instructionInputs.filter((input) => input.trim());
    const allInstructions = [...instructions, ...validInstructions];

    // Validate required fields
    if (!formData.subscriptionName.trim()) {
      toast.error("Subscription name is required");
      return;
    }
    if (!formData.price || parseFloat(formData.price) <= 0) {
      toast.error("Valid price is required");
      return;
    }
    if (!formData.cycle) {
      toast.error("Cycle is required");
      return;
    }
    if (!formData.itemName.trim()) {
      toast.error("Item name is required");
      return;
    }
    if (!formData.itemQuantity) {
      toast.error("Item quantity is required");
      return;
    }
    if (!formData.maxRedemptionLimit) {
      toast.error("Max redemption limit is required");
      return;
    }
    if (!formData.maxRedemptionLimitCycle) {
      toast.error("Max redemption limit cycle is required");
      return;
    }

    try {
      setIsSubmitting(true);

      // Prepare API payload
      const quantityValue = (formData.itemQuantity as any).value;
      const limitValue = (formData.maxRedemptionLimit as any).value;
      const payload: any = {
        title: formData.subscriptionName,
        description: formData.subscriptionDescription,
        price: formData.price,
        cycle: (formData.cycle as any).value,
        rules: allInstructions,
        is_active: isActive,
        items: [
          {
            item_type: "PRIMARY",
            quantity: quantityValue === "*" ? "*" : parseInt(quantityValue),
            item_name: formData.itemName,
            limit: parseInt(limitValue),
            limit_period: (formData.maxRedemptionLimitCycle as any).value,
          },
        ],
      };
      
      // Only add max_subscribers if it has a value
      if (formData.maxSubscribers && formData.maxSubscribers.trim()) {
        payload.max_subscribers = parseInt(formData.maxSubscribers);
      }

      let data: any;
      let error: any;
      
      // Check if we're updating or creating
      if (initialData?.uuid) {
        // UPDATE existing subscription
        const response = await http.patch(
          updateSubscription(businessUuid, initialData.uuid),
          payload
        );
        data = response.data;
        error = response.error;
      } else {
        // CREATE new subscription
        const response = await http.post(
          createSubscription(businessUuid),
          payload
        );
        data = response.data;
        error = response.error;
      }

      if (error) {
        // Helper function to extract error messages from nested structures
        const extractErrorMessages = (obj: any): string[] => {
          const messages: string[] = [];
          
          if (Array.isArray(obj)) {
            obj.forEach(item => {
              messages.push(...extractErrorMessages(item));
            });
          } else if (typeof obj === 'object' && obj !== null) {
            Object.values(obj).forEach(value => {
              if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
                // If it's an array of strings, those are the error messages
                messages.push(...value);
              } else {
                // Otherwise, recurse deeper
                messages.push(...extractErrorMessages(value));
              }
            });
          }
          
          return messages;
        };

        // Try to extract error message
        let errorMessage = error?.error?.[0] || error?.message;
        
        if (!errorMessage) {
          // Try to extract nested field errors
          const allMessages = extractErrorMessages(error);
          if (allMessages.length > 0) {
            errorMessage = allMessages[0]; // Show the first error
          }
        }
        
        toast.error(errorMessage || `Failed to ${initialData?.uuid ? 'update' : 'create'} subscription tier`);
        return;
      }

      if (data) {
        toast.success(
          initialData?.uuid
            ? "Subscription tier updated successfully" 
            : "Subscription tier created successfully"
        );
        onSubmit(data);
        onClose();
      }
    } catch (error) {
      toast.error(`Unexpected error occurred while ${initialData?.uuid ? 'updating' : 'creating'} subscription tier`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const addInstructionField = () => {
    setInstructionInputs([...instructionInputs, ""]);
  };

  const updateInstructionInput = (index: number, value: string) => {
    const updated = [...instructionInputs];
    updated[index] = value;
    setInstructionInputs(updated);
  };

  const removeInstructionInput = (index: number) => {
    setInstructionInputs(instructionInputs.filter((_, i) => i !== index));
  };

  const saveInstructions = () => {
    const validInstructions = instructionInputs.filter((input) => input.trim());
    if (validInstructions.length > 0) {
      setInstructions([...instructions, ...validInstructions]);
      setInstructionInputs([]);
    }
  };

  const deleteInstruction = (index: number) => {
    setInstructions(instructions.filter((_, i) => i !== index));
  };

  const updateInstruction = (index: number, value: string) => {
    const updated = [...instructions];
    updated[index] = value;
    setInstructions(updated);
  };

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <style>
        {`
          /* Hide number input spinners */
          input[type="number"]::-webkit-inner-spin-button,
          input[type="number"]::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          input[type="number"] {
            -moz-appearance: textfield;
          }

          /* Placeholder styling */
          input::placeholder,
          textarea::placeholder {
            font-family: 'Poppins', sans-serif;
            font-weight: 400;
            font-size: 14px;
            line-height: 100%;
            letter-spacing: 0%;
            opacity: 1;
          }
        `}
      </style>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-5">
        <InputField
          label="Subscription Name"
            type="text"
          placeholder="Enter subscription name..."
          value={formData.subscriptionName}
          onChange={(e) =>
            setFormData({ ...formData, subscriptionName: e.target.value })
          }
          containerClass="!mb-0"
        />

        <div>
          <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
            Subscription Description
          </label>
          <TextAreaField
            placeholder="Enter subscription description..."
            value={formData.subscriptionDescription}
            onChange={(e) =>
              setFormData({
                ...formData,
                subscriptionDescription: e.target.value,
              })
            }
            rows={3}
            className="!border-[#EDEDF0] resize-none"
            style={{ borderRadius: '4px' }}
            containerClassName="!mb-0"
          />
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Price
            </label>
            <div className="flex">
              <div className="flex items-center justify-center w-[42px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                <span className="text-gray-900 font-medium">$</span>
              </div>
              <InputField
                type="number"
                placeholder="Enter price..."
                value={formData.price}
                onChange={(e) =>
                  setFormData({ ...formData, price: e.target.value })
                }
                customClass="!border-l-0 !border-[#EDEDF0] !rounded-l-none"
                containerClass="!mb-0 flex-1"
              />
            </div>
          </div>

          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Cycle
            </label>
            <ReactSelect
              value={formData.cycle}
              options={cycleOptions}
              placeholder="Choose cycle..."
              isMulti={false}
              closeMenuOnSelect
              isOptionDisabled={(option: any) => option.value !== "MONTHLY"}
              onChange={(selectedOption: any) => {
                setFormData({ ...formData, cycle: selectedOption });
              }}
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: '#F6F5FC',
                  borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                  minHeight: '40px',
                  height: '40px',
                  borderRadius: '4px',
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: '40px',
                  padding: '0 8px',
                }),
                input: (base) => ({
                  ...base,
                  margin: '0',
                  padding: '0',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: '40px',
                }),
                singleValue: (base) => ({
                  ...base,
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#6B7280',
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: '#000000',
                  '& svg': {
                    width: '16px',
                    height: '16px',
                  },
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isDisabled
                    ? '#F3F4F6'
                    : state.isSelected
                    ? '#6366F1'
                    : state.isFocused
                    ? '#E0E7FF'
                    : base.backgroundColor,
                  color: state.isDisabled ? '#9CA3AF' : base.color,
                  cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                }),
              }}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:gap-4">
          <div className="flex-1">
            <InputField
              label="Item Name"
              type="text"
              placeholder="The products/services that subscribers will get..."
              value={formData.itemName}
              onChange={(e) =>
                setFormData({ ...formData, itemName: e.target.value })
              }
              containerClass="!mb-0"
            />
          </div>
          <div className="flex-1">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Item Quantity
            </label>
            <ReactSelect
              value={formData.itemQuantity}
              options={itemQuantityOptions}
              placeholder="Choose quantity..."
              isMulti={false}
              closeMenuOnSelect
              onChange={(selectedOption: any) => {
                setFormData({ ...formData, itemQuantity: selectedOption });
              }}
              styles={{
                control: (base, state) => ({
                  ...base,
                  backgroundColor: '#FFFFFF',
                  borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                  minHeight: '40px',
                  height: '40px',
                  borderRadius: '4px',
                }),
                valueContainer: (base) => ({
                  ...base,
                  height: '40px',
                  padding: '0 8px',
                }),
                input: (base) => ({
                  ...base,
                  margin: '0',
                  padding: '0',
                }),
                indicatorsContainer: (base) => ({
                  ...base,
                  height: '40px',
                }),
                placeholder: (base) => ({
                  ...base,
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#9CA3AF',
                }),
                singleValue: (base) => ({
                  ...base,
                  fontFamily: 'Poppins, sans-serif',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '100%',
                  letterSpacing: '0%',
                  color: '#6B7280',
                }),
                indicatorSeparator: () => ({
                  display: 'none',
                }),
                dropdownIndicator: (base) => ({
                  ...base,
                  color: '#000000',
                  '& svg': {
                    width: '16px',
                    height: '16px',
                  },
                }),
              }}
            />
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-3 md:gap-4 items-start">
          <div className="flex-1 w-full">
            <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
              Limit
            </label>
            <div className="flex gap-2">
              <div className="flex-1">
                <ReactSelect
                  value={formData.maxRedemptionLimit}
                  options={limitOptions}
                  placeholder="Choose limit..."
                  isMulti={false}
                  closeMenuOnSelect
                  onChange={(selectedOption: any) => {
                    setFormData({ ...formData, maxRedemptionLimit: selectedOption });
                  }}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: '#FFFFFF',
                      borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                      minHeight: '40px',
                      height: '40px',
                      borderRadius: '4px',
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      height: '40px',
                      padding: '0 8px',
                    }),
                    input: (base) => ({
                      ...base,
                      margin: '0',
                      padding: '0',
                    }),
                    indicatorsContainer: (base) => ({
                      ...base,
                      height: '40px',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#9CA3AF',
                    }),
                    singleValue: (base) => ({
                      ...base,
                      fontFamily: 'Poppins, sans-serif',
                      fontWeight: 400,
                      fontSize: '14px',
                      lineHeight: '100%',
                      letterSpacing: '0%',
                      color: '#6B7280',
                    }),
                    indicatorSeparator: () => ({
                      display: 'none',
                    }),
                    dropdownIndicator: (base) => ({
                      ...base,
                      color: '#000000',
                      '& svg': {
                        width: '16px',
                        height: '16px',
                      },
                    }),
                  }}
              />
              </div>
              <div className="flex" style={{ width: '254.5px', gap: '1px' }}>
                <div className="flex items-center justify-center w-[56px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                  <span className="text-gray-900 text-sm font-medium">per</span>
                </div>
                <div className="flex-1">
                  <ReactSelect
                    value={formData.maxRedemptionLimitCycle}
                    options={redemptionCycleOptions}
                    placeholder="Select cycle"
                    isMulti={false}
                    closeMenuOnSelect
                    isOptionDisabled={(option: any) => option.value !== "PER_DAY" && option.value !== "PER_WEEK"}
                    onChange={(selectedOption: any) => {
                      setFormData({ ...formData, maxRedemptionLimitCycle: selectedOption });
                    }}
                    styles={{
                      control: (base, state) => ({
                        ...base,
                        backgroundColor: '#FFFFFF',
                        borderColor: state.isFocused ? '#6366F1' : '#EDEDF0',
                        borderLeft: 'none',
                        minHeight: '40px',
                        height: '40px',
                        borderRadius: 0,
                        borderTopRightRadius: '4px',
                        borderBottomRightRadius: '4px',
                      }),
                      valueContainer: (base) => ({
                        ...base,
                        height: '40px',
                        padding: '0 8px',
                      }),
                      input: (base) => ({
                        ...base,
                        margin: '0',
                        padding: '0',
                      }),
                      indicatorsContainer: (base) => ({
                        ...base,
                        height: '40px',
                      }),
                      singleValue: (base) => ({
                        ...base,
                        color: '#6B7280',
                      }),
                      indicatorSeparator: () => ({
                        display: 'none',
                      }),
                      dropdownIndicator: (base) => ({
                        ...base,
                        color: '#000000',
                        '& svg': {
                          width: '16px',
                          height: '16px',
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isDisabled
                          ? '#F3F4F6'
                          : state.isSelected
                          ? '#6366F1'
                          : state.isFocused
                          ? '#E0E7FF'
                          : base.backgroundColor,
                        color: state.isDisabled ? '#9CA3AF' : base.color,
                        cursor: state.isDisabled ? 'not-allowed' : 'pointer',
                      }),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 w-full">
            <InputField
              label="Max Subscribers"
              type="number"
              placeholder="Enter max amount of people who can subscribe..."
              value={formData.maxSubscribers}
              onChange={(e) =>
                setFormData({ ...formData, maxSubscribers: e.target.value })
              }
              containerClass="!mb-0"
            />
          </div>
        </div>

        <div>
          <label className="block mb-2 font-poppins text-sm font-medium leading-none text-[#333333CC]">
          Store Rules
          </label>
          
          {instructions.length > 0 && (
            <div className="mb-3 flex flex-col gap-[10px]">
              {instructions.map((instruction, index) => (
                <div
                  key={index}
                  className="flex items-center gap-[10px]"
                >
                  <div className="flex flex-1">
                    <div className="flex items-center justify-center w-[41px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                      <span className="text-gray-900 font-medium text-base sm:text-lg">{index + 1}.</span>
                    </div>
                    <InputField
                      type="text"
                      value={instruction}
                      onChange={(e) => updateInstruction(index, e.target.value)}
                      placeholder="Enter instruction..."
                      customClass="!border-l-0 !border-[#EDEDF0] !rounded-l-none"
                      containerClass="!mb-0 flex-1"
                    />
                  </div>
                  <IconButton
                    icon={<IoTrashOutline size={22} />}
                    onClick={() => deleteInstruction(index)}
                    variant="danger"
                    ariaLabel="Delete instruction"
                  />
                </div>
              ))}
            </div>
          )}

          {instructionInputs.map((input, index) => (
            <div 
              key={index} 
              className="flex items-center mb-3 gap-[10px]"
            >
              <div className="flex flex-1">
                <div className="flex items-center justify-center w-[41px] h-[40px] px-4 py-[10px] bg-[#EDEDF0] border border-r-0 border-[#EDEDF0]" style={{ borderTopLeftRadius: '4px', borderBottomLeftRadius: '4px' }}>
                  <span className="text-gray-900 font-medium text-base sm:text-lg">{instructions.length + index + 1}.</span>
                </div>
                <InputField
                  type="text"
                  placeholder="Enter instruction..."
                  value={input}
                  onChange={(e) => updateInstructionInput(index, e.target.value)}
                  customClass="!border-l-0 !border-[#EDEDF0] !rounded-l-none"
                  containerClass="!mb-0 flex-1"
                />
              </div>
              <IconButton
                icon={<IoTrashOutline size={22} />}
                onClick={() => removeInstructionInput(index)}
                variant="danger"
                ariaLabel="Remove instruction"
              />
            </div>
          ))}

          <button
            type="button"
            onClick={addInstructionField}
            className="flex items-center gap-2 font-poppins font-medium hover:opacity-80 transition-opacity text-sm leading-none text-[#0A84FF] bg-transparent"
          >
            <AddCircle20Regular className="w-5 h-5 text-[#0A84FF]" />
            <span>{instructionInputs.length > 0 ? 'Add Another Rule' : 'Add Rules'}</span>
          </button>
        </div>

        <div className="flex items-center gap-3">
          <label className="font-poppins text-sm font-medium leading-none text-[#333333CC]">
            Activate
          </label>
          <label className="relative inline-flex items-center">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-9 h-[18px] p-[2px] rounded-[120px] relative bg-gray-300 peer-checked:bg-primary">
              <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white flex items-center justify-center text-[8px] font-bold ${
                isActive ? 'left-[calc(100%-16px)] text-primary' : 'left-[2px] text-gray-400'
              }`}>
                {isActive ? '✓' : '−'}
              </div>
            </div>
          </label>
        </div>

        <div>
          <label className="block mb-2 font-poppins text-base font-medium leading-none text-[#0B1215]">
            Preview
          </label>
          {formData.subscriptionName || formData.price || formData.itemName || instructions.length > 0 || instructionInputs.some(input => input.trim()) || formData.maxSubscribers ? (
            <div className="font-poppins w-full max-w-[800px] text-sm font-semibold leading-none">
              <div className="flex flex-col gap-3">
                {formData.subscriptionName && (
                  <div>
                    {formData.subscriptionName} :
                    <div className="mt-2 space-y-1">
                      {(formData.price || formData.itemQuantity || formData.itemName) && (
                        <div className="font-normal">
                          Subscriber will pay{" "}
                          <span className="font-semibold">
                            ${formData.price || "0"}
                          </span>{" "}
                          for{" "}
                          <span className="font-semibold">
                            {formData.itemQuantity 
                              ? ((formData.itemQuantity as any).value === "*" 
                                  ? "unlimited" 
                                  : (formData.itemQuantity as any).value) 
                              : "0"} x {formData.itemName || "Item"}
                          </span>{" "}
                          per {formData.cycle ? (formData.cycle as any).label.toLowerCase() : "month"}
                          {formData.maxRedemptionLimit && formData.maxRedemptionLimitCycle && (
                            <>, limit <span className="font-semibold">{(formData.maxRedemptionLimit as any).value}</span> per <span className="font-semibold">{(formData.maxRedemptionLimitCycle as any).label}</span></>
                          )}
                        </div>
                      )}
                      {(instructions.length > 0 || instructionInputs.filter(input => input.trim()).length > 0) && (
                        <ul className="list-disc list-inside space-y-1 ml-6">
                          {instructions.map((instruction, index) => (
                            <li key={index}>
                              {instruction}
                            </li>
                          ))}
                          {instructionInputs.filter(input => input.trim()).map((instruction, index) => (
                            <li key={`input-${index}`}>
                              {instruction}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}
                {formData.maxSubscribers && (
                  <div>
                    <span className="font-normal">Max</span> <span className="font-semibold">{formData.maxSubscribers}</span> <span className="font-normal">subscriptions</span>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              Preview of what customers will see
            </p>
          )}
        </div>

        <div className="flex justify-start pt-2">
          <Button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isSubmitting}
            loading={isSubmitting}
            label="Save Subscription"
            customClass="!w-[167px] !h-[45px] !py-3 !px-5 !border !rounded-full !mb-0 whitespace-nowrap !font-poppins !font-medium !text-sm !leading-none !text-center"
          />
        </div>
      </form>

      {/* Cancel Confirmation Modal */}
      <Render when={showCancelConfirmModal}>
        <ModalComponent
          showClose={false}
          containerClass="!p-0 !max-w-[95vw] !w-full md:!w-[416px] !rounded-[12px]"
          onClose={() => setShowCancelConfirmModal(false)}
        >
          <div 
            className="flex flex-col items-center text-center pt-8 px-5 pb-8"
          >
            {/* Warning Icon */}
            <div className="rounded-full bg-red-100 flex items-center justify-center w-[50px] h-[50px] mb-10">
              <svg
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#EF4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>

            {/* Text Section with reduced gap */}
            <div className="flex flex-col items-center gap-5 w-full mb-4">
            {/* Title */}
              <h2 className="w-full font-poppins font-medium text-[20px] leading-none text-center text-textDark">
              Cancel Subscription Setup?
            </h2>

            {/* Description */}
              <p className="w-full max-w-[376px] font-poppins font-normal text-sm leading-relaxed text-center text-[#757575] px-4">
              You're about to cancel creating this subscription for your business. All details you've entered so far will be lost and won't be saved. Are you sure you want to cancel?
            </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-row gap-3 md:gap-4 w-full justify-center">
              <Button
                onClick={() => setShowCancelConfirmModal(false)}
                label="Cancel"
                variant="secondary"
                customClass="!w-[83px] !h-[38px] !py-[10px] !px-5 !border !border-[#FB4C2F] !text-[#FB4C2F] hover:!bg-red-50 !mb-0 !rounded-full !font-poppins !font-medium !text-xs !leading-none !text-center"
              />
              <Button
                onClick={() => {
                  setShowCancelConfirmModal(false);
                  onClose();
                }}
                label="Continue"
                customClass="!w-[96px] !h-[38px] !py-[10px] !px-5 !mb-0 !rounded-full !font-poppins !font-medium !text-xs !leading-none !text-center !text-white"
              />
            </div>
          </div>
        </ModalComponent>
      </Render>
    </div>
  );
};

export default AddSubscriptionTierModal;

